import React, { useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { SuggestionProps } from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import api from 'config/axiosConfig';
import { PluginKey } from '@tiptap/pm/state';
import { parseWikipediaData, getWikipediaSummary } from 'util/wikiUtils';
import { Plus, Loader2 } from 'lucide-react';
import { createIssueFromWikipedia } from 'util/issueCreationUtils';
import { LAYER_TOKENS } from 'ui/layers';
import { apiRoutes } from 'util/apiRoutes';

interface HashtagItem {
  id?: string;
  tag: string;
}

interface IssueItem {
  type: 'issue' | 'wiki';
  id?: number;
  name: string;
  cname: string;
  wikipedia_link?: string;
  link?: string;
  thumbnail?: string;
  subject_properties?: any;
  short_description?: string;
  properties?: any;
}

type SuggestionItem = 
  | { type: 'hashtag'; data: HashtagItem }
  | { type: 'issue'; data: IssueItem };

interface SuggestionItemProps {
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  isLoading?: boolean;
  hasMoreResults?: boolean;
  onLoadMore?: () => void;
  query?: string;
}

interface SuggestionListHandle {
  onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
}

const SuggestionList = forwardRef<SuggestionListHandle, SuggestionItemProps>(
  ({ items, command, isLoading = false, hasMoreResults = false, onLoadMore, query = '' }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;
    const listRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prevIndex) => (prevIndex + items.length - 1) % items.length);
          event.preventDefault();
          return true;
        } else if (event.key === 'ArrowDown') {
          setSelectedIndex((prevIndex) => (prevIndex + 1) % items.length);
          event.preventDefault();
          return true;
        } else if (event.key === 'Enter') {
          selectItem(selectedIndex);
          event.preventDefault();
          return true;
        }
        return false;
      },
    }));

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // Scroll selected item into view
    useEffect(() => {
      if (listRef.current) {
        const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }, [selectedIndex]);

    // Handle scroll for infinite loading
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      
      if (isNearBottom && hasMoreResults && !isLoading && onLoadMore) {
        onLoadMore();
      }
    };

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    };

    return (
      <div 
        ref={listRef}
        onScroll={handleScroll}
        className="bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main rounded-md shadow-lg max-h-60 overflow-auto overflow-x-clip"
        style={{ zIndex: LAYER_TOKENS.prioritySurface }}
      >
        {items.map((item, index) => (
          <div
            key={item.type === 'hashtag' ? item.data.id || item.data.tag : `${item.data.cname}-${index}`}
            className={`px-2 py-2 my-1 cursor-pointer flex items-center justify-between ${
              index === selectedIndex ? 'bg-gray-600 dark:bg-gray-700' : 'hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            onClick={() => selectItem(index)}
          >
            {item.type === 'hashtag' ? (
              <>
                <span className="text-eoe-text dark:text-eoe-dark-text">#{item.data.tag}</span>
                <span className="flex flex-shrink-0 rounded-full px-2 text-xs bg-eoe-entity-tag text-white items-center justify-center">Tag</span>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <span className="w-6 h-6 flex-shrink-0">
                    <img 
                      src={
                        item.data.type === 'wiki' && item.data.thumbnail && item.data.thumbnail.trim() !== '' 
                          ? item.data.thumbnail 
                          : item.data.properties?.reference_link_img_key 
                            ? `${IMAGE_CDN}/${item.data.properties.reference_link_img_key}` 
                            : item.data.properties?.subject_properties?.thumbnail?.source 
                              ? item.data.properties.subject_properties.thumbnail.source
                              : 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'
                      } 
                      alt="Issue avatar" 
                      className="w-full h-full rounded-full object-cover" 
                    />
                  </span>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className={`text-sm truncate ${item.data.type === 'wiki' ? 'italic' : ''} text-eoe-text dark:text-eoe-dark-text`}>
                      <span className="inline-flex items-center">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-medium bg-soc-purple text-white mr-1">
                          #
                        </span>
                        <span className="font-bold">
                          {item.data.name}
                        </span>
                      </span>
                    </span>
                    {item.data.short_description && (
                      <span className={`text-xs truncate ${item.data.type === 'wiki' ? 'italic' : ''} text-eoe-text-sub dark:text-eoe-dark-text-sub ml-5`}>
                        {item.data.short_description}
                      </span>
                    )}
                  </div>
                </div>
                {item.data.type === 'wiki' ? (
                  <div className="flex items-center space-x-0 p-1 rounded-full group-hover:bg-eoe-icon-hover dark:group-hover:bg-eoe-dark-icon-hover text-eoe-gray dark:text-eoe-dark-text flex-shrink-0">
                    <img src='https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg' alt='Wikipedia Logo' className="w-4 h-4 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" />
                    <Plus className="w-4 h-4 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" />
                  </div>
                ) : (
                  <span className="flex rounded-full px-2 text-xs bg-soc-purple text-white flex-shrink-0 items-center justify-center">Issue</span>
                )}
              </>
            )}
          </div>
        ))}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="px-2 py-2 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-eoe-text dark:text-eoe-dark-text" />
            <span className="ml-2 text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">Loading more...</span>
          </div>
        )}
        
        {/* End of results indicator */}
        {!hasMoreResults && items.length > 10 && !isLoading && (
          <div className="px-2 py-1 text-center text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta border-t border-eoe-border-main dark:border-eoe-dark-border-main">
            End of results
          </div>
        )}
      </div>
    );
  }
);

/**
 * Search state manager for combined hashtag and issue search
 */
class SearchStateManager {
  private hashtagOffset = 0;
  private issueOffset = 0;
  private wikiLimit = 0;
  private loadedWikiTitles = new Set<string>();
  private hashtags: HashtagItem[] = [];
  private issues: IssueItem[] = [];
  private isLoadingHashtags = false;
  private isLoadingIssues = false;
  private hasMoreHashtags = true;
  private hasMoreIssues = true;
  private currentQuery = '';
  private updateCallback: (() => void) | null = null;
  private abortController: AbortController | null = null;

  private readonly HASHTAG_PAGE_SIZE = 20;
  private readonly ISSUE_PAGE_SIZE = 5;
  private readonly WIKI_PAGE_SIZE = 5;
  private readonly INITIAL_ISSUE_LIMIT = 10;

  setUpdateCallback(callback: () => void) {
    this.updateCallback = callback;
  }

  private triggerUpdate() {
    if (this.updateCallback) {
      this.updateCallback();
    }
  }

  reset() {
    this.hashtagOffset = 0;
    this.issueOffset = 0;
    this.wikiLimit = 0;
    this.loadedWikiTitles.clear();
    this.hashtags = [];
    this.issues = [];
    this.isLoadingHashtags = false;
    this.isLoadingIssues = false;
    this.hasMoreHashtags = true;
    this.hasMoreIssues = true;
    this.currentQuery = '';
    
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async performInitialSearch(query: string) {
    if (!query) {
      this.reset();
      this.triggerUpdate();
      return;
    }

    this.reset();
    this.currentQuery = query;
    
    // Start both searches in parallel
    await Promise.all([
      this.searchHashtags(query, true),
      this.searchIssues(query, true)
    ]);
  }

  async searchHashtags(query: string, isInitial: boolean = false) {
    const isValid = /^[a-zA-Z0-9]+$/.test(query);
    if (!isValid) {
      this.hashtags = [];
      this.hasMoreHashtags = false;
      this.triggerUpdate();
      return;
    }

    if (this.isLoadingHashtags) {
      return;
    }

    this.isLoadingHashtags = true;
    this.triggerUpdate();

    try {
      const offset = isInitial ? 0 : this.hashtagOffset;
      const response = await api.get<{ tags: HashtagItem[], total: number, hasMore: boolean }>(
        apiRoutes.tags.search,
        {
          params: {
            q: query,
            offset,
            limit: this.HASHTAG_PAGE_SIZE,
          },
        }
      );

      const hashtagResults = response.data.tags.filter(tag => /^[a-zA-Z0-9]+$/.test(tag.tag));

      if (isInitial) {
        this.hashtags = hashtagResults;
        this.hashtagOffset = 0;
      } else {
        this.hashtags = [...this.hashtags, ...hashtagResults];
      }

      this.hashtagOffset += hashtagResults.length;
      this.hasMoreHashtags = response.data.hasMore;
    } catch (error) {
      console.error('Error fetching hashtags:', error);
      this.hasMoreHashtags = false;
    } finally {
      this.isLoadingHashtags = false;
      this.triggerUpdate();
    }
  }

  async searchIssues(query: string, isInitial: boolean = false) {
    if (!query) {
      this.issues = [];
      this.hasMoreIssues = false;
      this.triggerUpdate();
      return;
    }

    if (this.isLoadingIssues) {
      return;
    }

    this.isLoadingIssues = true;
    this.triggerUpdate();

    try {
      const offset = isInitial ? 0 : this.issueOffset;
      
      // 1. Fetch issue suggestions from backend
      const issueResponse = await api.get(apiRoutes.issues.root, {
        params: {
          search: query,
          excludeCompoundIssues: true,
          offset,
          limit: this.ISSUE_PAGE_SIZE,
        },
      });

      const issueResults = issueResponse.data;
      const issueSuggestions: IssueItem[] = issueResults.map((issue: any) => ({
        ...issue,
        type: 'issue' as const
      }));

      // 2. Calculate Wikipedia results needed
      let currentWikiLimit = 0;
      if (isInitial) {
        const remainingResults = Math.max(0, this.INITIAL_ISSUE_LIMIT - issueSuggestions.length);
        currentWikiLimit = remainingResults;
        this.wikiLimit = currentWikiLimit;
      } else {
        currentWikiLimit = this.wikiLimit + this.WIKI_PAGE_SIZE;
        this.wikiLimit = currentWikiLimit;
      }

      // 3. Fetch from Wikipedia if needed
      let wikiSuggestions: IssueItem[] = [];
      if (currentWikiLimit > 0) {
        const wikiResponse = await fetch(
          `https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(
            query
          )}&limit=${currentWikiLimit}&namespace=0&format=json&origin=*`
        );
        const data = await wikiResponse.json();

        const existingCnames = new Set([
          ...issueResults.map((issue: any) => issue.cname.toLowerCase()),
          ...(isInitial ? [] : this.issues.filter(i => i.type === 'issue').map(i => i.cname.toLowerCase()))
        ]);

        // Filter out titles we've already loaded
        const newWikiTitles = data[1].filter((title: string) =>
          !this.loadedWikiTitles.has(title.toLowerCase())
        );

        const allWiki = await Promise.all(
          newWikiTitles.map(async (title: string) => {
            try {
              const summaryData = await getWikipediaSummary(title);
              if (!summaryData) {
                return null;
              }
              const canonicalTitle = summaryData.canonicalTitle;

              if (existingCnames.has(canonicalTitle.toLowerCase())) {
                return null;
              }

              try {
                const existingIssueResponse = await api.get(apiRoutes.issues.byName(encodeURIComponent(canonicalTitle)));

                if (existingIssueResponse.status === 200) {
                  return {
                    type: 'issue' as const,
                    ...existingIssueResponse.data,
                    cname: canonicalTitle
                  };
                }
              } catch (error) {
                const subjectParseProperties = await parseWikipediaData(canonicalTitle, false);
                return {
                  type: 'wiki' as const,
                  name: summaryData.title,
                  link: summaryData.wikipediaUrl,
                  thumbnail: summaryData.thumbnail || undefined,
                  cname: canonicalTitle,
                  subject_properties: {
                    ...summaryData,
                    ...subjectParseProperties,
                  },
                  short_description: summaryData.description,
                };
              }
              return null;
            } catch (error) {
              console.error('Error fetching page summary:', error);
              return null;
            }
          })
        );

        wikiSuggestions = allWiki.filter((item): item is IssueItem => item !== null);

        // Update loaded wiki titles
        newWikiTitles.forEach((title: string) => {
          this.loadedWikiTitles.add(title.toLowerCase());
        });
      }

      // 4. Update issues
      if (isInitial) {
        this.issues = [...issueSuggestions, ...wikiSuggestions];
        this.issueOffset = 0;
      } else {
        this.issues = [...this.issues, ...issueSuggestions, ...wikiSuggestions];
      }

      this.issueOffset += issueSuggestions.length;
      
      // Check if there are more results
      const hasMoreBackendResults = issueResults.length === this.ISSUE_PAGE_SIZE;
      const hasMoreWikiResults = wikiSuggestions.length > 0 || currentWikiLimit < 500;
      this.hasMoreIssues = hasMoreBackendResults || hasMoreWikiResults;

    } catch (error) {
      console.error('Error fetching issues:', error);
      this.hasMoreIssues = false;
    } finally {
      this.isLoadingIssues = false;
      this.triggerUpdate();
    }
  }

  async loadMore() {
    const promises = [];
    
    if (this.hasMoreHashtags && !this.isLoadingHashtags) {
      promises.push(this.searchHashtags(this.currentQuery, false));
    }
    
    if (this.hasMoreIssues && !this.isLoadingIssues) {
      promises.push(this.searchIssues(this.currentQuery, false));
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  getCombinedResults(): SuggestionItem[] {
    const hashtagSuggestions: SuggestionItem[] = this.hashtags.map(tag => ({
      type: 'hashtag' as const,
      data: tag
    }));

    const issueSuggestions: SuggestionItem[] = this.issues.map(issue => ({
      type: 'issue' as const,
      data: issue
    }));

    const combined = [...hashtagSuggestions, ...issueSuggestions];

    // Include current query as hashtag option if no exact matches
    const isValid = /^[a-zA-Z0-9]+$/.test(this.currentQuery);
    if (isValid && !combined.some(s => 
      s.type === 'hashtag' && s.data.tag.toLowerCase() === this.currentQuery.toLowerCase()
    )) {
      combined.unshift({
        type: 'hashtag' as const,
        data: { tag: this.currentQuery }
      });
    }

    return combined;
  }

  isLoading(): boolean {
    return this.isLoadingHashtags || this.isLoadingIssues;
  }

  hasMore(): boolean {
    return this.hasMoreHashtags || this.hasMoreIssues;
  }
}

// Global search manager instance
const searchManager = new SearchStateManager();

const hashtagSuggestion = {
  char: '#',
  pluginKey: new PluginKey('hashtagSuggestion'),
  startOfLine: false,
  items: async ({ query }: { query: string }) => {
    // Perform search through the search manager
    await searchManager.performInitialSearch(query);
    return searchManager.getCombinedResults();
  },
  command: async ({ editor, range, props }: any) => {
    const item = props as SuggestionItem;
    
    if (item.type === 'hashtag') {
      const isValid = /^[a-zA-Z0-9]+$/.test(item.data.tag);
      if (!isValid) return;
      
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'hashtag',
            attrs: {
              label: item.data.tag,
            },
          },
          { type: 'text', text: ' ' },
        ])
        .run();
    } else {
      // Handle issue tagging
      let issueToTag = item.data;
      
      // If it's a Wikipedia suggestion, create the issue first
      if (issueToTag.type === 'wiki') {
        try {
          const wikiSuggestion = {
            type: 'wiki' as const,
            name: issueToTag.name,
            link: issueToTag.link || '',
            thumbnail: issueToTag.thumbnail || '',
            cname: issueToTag.cname,
            subject_properties: issueToTag.subject_properties,
            short_description: issueToTag.short_description
          };
          const createdIssue = await createIssueFromWikipedia(wikiSuggestion);
          issueToTag = {
            ...createdIssue,
            type: 'issue' as const
          };
        } catch (error) {
          console.error('Error creating issue from Wikipedia:', error);
          return;
        }
      }
      
      // Insert issue hashtag with issue_tag_ prefix
      const issueTagLabel = `issue_tag_${issueToTag.cname}`;
      
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'hashtag',
            attrs: {
              label: issueTagLabel,
              issueData: {
                id: issueToTag.id,
                name: issueToTag.name,
                cname: issueToTag.cname,
                type: 'issue'
              }
            },
          },
          { type: 'text', text: ' ' },
        ])
        .run();
    }
  },
  render: () => {
    let component: ReactRenderer<SuggestionListHandle>;
    let popup: TippyInstance[];
    let currentQuery = '';

    return {
      onStart: (props: SuggestionProps) => {
        currentQuery = props.query || '';
        
        // Set up callback to update component when search completes
        searchManager.setUpdateCallback(() => {
          if (component) {
            component.updateProps({
              items: searchManager.getCombinedResults(),
              isLoading: searchManager.isLoading(),
              hasMoreResults: searchManager.hasMore(),
              query: currentQuery,
              onLoadMore: () => searchManager.loadMore(),
            } as any);
          }
        });

        component = new ReactRenderer(SuggestionList, {
          props: {
            ...props,
            items: searchManager.getCombinedResults(),
            isLoading: searchManager.isLoading(),
            hasMoreResults: searchManager.hasMore(),
            query: currentQuery,
            onLoadMore: () => searchManager.loadMore(),
          } as any,
          editor: props.editor,
        });

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as any,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          placement: 'bottom-start',
          zIndex: LAYER_TOKENS.prioritySurface,
        });
      },
      onUpdate(props: SuggestionProps) {
        currentQuery = props.query || '';
        
        component.updateProps({
          ...props,
          items: searchManager.getCombinedResults(),
          isLoading: searchManager.isLoading(),
          hasMoreResults: searchManager.hasMore(),
          query: currentQuery,
          onLoadMore: () => searchManager.loadMore(),
        } as any);

        popup[0].setProps({
          getReferenceClientRect: props.clientRect as any,
        });
      },
      onKeyDown({ event }: { event: KeyboardEvent }) {
        if (event.key === 'Escape') {
          popup[0].hide();
          return true;
        }

        return component.ref?.onKeyDown({ event }) ?? false;
      },
      onExit() {
        popup[0].destroy();
        component.destroy();
        searchManager.setUpdateCallback(() => {});
      },
    };
  },
};

export default hashtagSuggestion;
